import React, { useEffect, useState } from "react";
import Typography from "./Typography";
import { X } from "lucide-react";
import Button from "./Button";

type ModalProps = {
  activeModal: boolean;
  onClose: () => void;
  noFade?: boolean;
  disableBackdrop?: boolean;
  className?: string;
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  centered?: boolean;
  scrollContent?: boolean;
  themeClass?: string;
  title?: string | React.ReactNode;
  titleClass?: string;
  closeIconClass?: string;
  bodyClass?: string;
  footerClass?: string;
  dialogClass?: string;
  modalClose?: () => void;
  hideHeader?: boolean;
  modalSize?: "small" | "medium" | "large" | "full";
};

const Modal: React.FC<ModalProps> = ({
  activeModal,
  onClose,
  noFade,
  disableBackdrop,
  className = "",
  children,
  footerContent,
  centered,
  scrollContent,
  themeClass = "border-b bg-white dark:bg-slate-800 dark:border-b dark:border-slate-700",
  title = "Basic Modal",
  titleClass = "text-slate-800 dark:text-white",
  bodyClass = "p-6",
  footerClass = "",
  dialogClass = "",
  modalClose,
  hideHeader = false,
  modalSize,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && activeModal) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [activeModal, onClose]);

  useEffect(() => {
    if (activeModal) {
      setTimeout(() => setIsVisible(true), 10);
      document.body.style.overflow = "hidden";
    } else {
      setIsVisible(false);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModal]);

  const getModalSizeClass = () => {
    if (!modalSize) return "w-auto"; // Default to content width
    switch (modalSize) {
      case "small":
        return "w-1/4 h-auto max-h-[80vh]";
      case "medium":
        return "w-1/2 h-auto max-h-[85vh]";
      case "large":
        return "w-3/4 h-auto max-h-[90vh]";
      case "full":
        return "w-full h-full max-h-screen";
      default:
        return "w-auto";
    }
  };

  if (!activeModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div
        className={`rounded-lg bg-white dark:bg-slate-800 shadow-xl ${getModalSizeClass()} ${dialogClass} duration-300 overflow-y-auto ${
          noFade ? "" : isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        {!hideHeader && (
          <div className={` flex justify-between p-4 sticky top-0 z-10 ${themeClass}`}>
            <Typography variant="h6" className={`font-medium capitalize tracking-wider ${titleClass}`}>
              {title}
            </Typography>
            <Button
              variant="text"
              size="none"
              className="!text-border"
              endIcon={<X size={20} strokeWidth={2} />}
              onClick={modalClose || onClose}
            />
          </div>
        )}

        <div className={`${bodyClass} ${scrollContent ? "max-h-[70vh] relative overflow-y-auto" : ""}`}>
          {children}
        </div>

        {footerContent && (
          <div className={`border-t border-slate-100 px-4 py-3 dark:border-slate-700 ${footerClass}`}>
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
